/**
 * O regime de prazos de cada concessão (`OB-02`, ADR-057): de onde cada prazo conta, quanto
 * dura e de onde vem a regra — e a transição do CONTRAN, enquanto ela vale.
 *
 * Só leitura, para quem abre a SENATRAN. Trocar o regime é um comando na VM, como dono do
 * banco: regime é fato do contrato e muda dinheiro (decisão de 07/10). As concessões no mesmo
 * regime aparecem juntas, com uma tabela só.
 */
import { useEffect, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type { RegimeDaConcessaoNaTela } from "../api/tipos";
import { duracaoDoRelogio, ultimoDia } from "../formato";
import { Badge, Estado } from "./base";

function dia(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo",
  });
}

export function RegimeDePrazos() {
  const [itens, definirItens] = useState<RegimeDaConcessaoNaTela[] | null>(
    null,
  );
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api<RegimeDaConcessaoNaTela[]>("/tollvision/regimes")
      .then((r) => vivo && definirItens(r))
      .catch(
        (falha) =>
          vivo &&
          definirErro(
            falha instanceof ErroDaApi
              ? falha.message
              : "Não foi possível ler o regime de prazos.",
          ),
      );
    return () => {
      vivo = false;
    };
  }, []);

  if (erro) return <Estado tipo="erro" titulo={erro} />;
  if (itens === null)
    return <Estado tipo="carregando" titulo="Lendo o regime de prazos…" />;
  if (itens.length === 0)
    return <Estado tipo="vazio" titulo="Nenhuma concessão no seu recorte." />;

  // As concessões no mesmo regime, juntas: a tabela é a mesma.
  const grupos = new Map<string, RegimeDaConcessaoNaTela[]>();
  for (const item of itens) {
    grupos.set(item.regime.codigo, [
      ...(grupos.get(item.regime.codigo) ?? []),
      item,
    ]);
  }

  return (
    <section
      id="regime-de-prazos"
      aria-label="Regime de prazos"
      className="grid gap-3 rounded-cartao border border-linha bg-painel p-3"
    >
      {[...grupos.values()].map((concessoes) => {
        const regime = concessoes[0]!.regime;
        const transicao = regime.transicoes.find((t) => t.vigente);
        return (
          <div key={regime.codigo} className="grid gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge cor={regime.pendente ? "amarelo" : "azul"}>
                {regime.nome}
              </Badge>
              <span className="text-[11.5px] text-tinta-2">
                {concessoes
                  .map(
                    (c) =>
                      `${c.concessao}${c.desde ? ` (desde ${dia(c.desde)})` : ""}`,
                  )
                  .join(" · ")}
              </span>
              <span className="text-[11px] text-tinta-3">
                Autuador: {regime.autuador}
              </span>
            </div>

            {transicao && (
              <p className="rounded-cartao border border-amarelo-borda bg-amarelo-fundo px-2.5 py-1.5 text-[11.5px] text-tinta-2">
                <strong className="text-amarelo">
                  Transição até {ultimoDia(transicao.fim)}.
                </strong>{" "}
                {transicao.descricao}{" "}
                <span className="text-tinta-3">({transicao.fonte})</span>
              </p>
            )}

            <table className="w-full text-left text-[11.5px]">
              <thead className="text-rotulo tracking-[0.06em] text-tinta-3 uppercase">
                <tr>
                  <th className="py-1 pr-2 font-bold">Prazo</th>
                  <th className="py-1 pr-2 font-bold">Duração</th>
                  <th className="py-1 pr-2 font-bold">Conta de</th>
                  <th className="hidden py-1 font-bold md:table-cell">Fonte</th>
                </tr>
              </thead>
              <tbody>
                {regime.relogios.map((r) => (
                  <tr key={r.tipo} className="border-t border-linha-suave">
                    <td className="py-1 pr-2 text-tinta-2" title={r.descricao}>
                      {r.rotulo}
                    </td>
                    <td className="py-1 pr-2 text-tinta tabular-nums">
                      <span className="whitespace-nowrap">
                        {duracaoDoRelogio(r.duracao_horas)}
                      </span>
                      {transicao && r.estendido_pela_transicao && (
                        <span className="block text-amarelo md:inline">
                          <span className="hidden md:inline"> </span>ou o fim da
                          transição
                        </span>
                      )}
                    </td>
                    <td className="py-1 pr-2 text-tinta-2">{r.conta_de}</td>
                    <td className="hidden py-1 text-tinta-3 md:table-cell">
                      {r.fonte}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {regime.pendente && (
              <p className="text-[11px] text-tinta-3">
                A confirmar: {regime.pendente}
              </p>
            )}
            {concessoes
              .filter((c) => c.proximo)
              .map((c) => (
                <p key={c.concessao_id} className="text-[11px] text-tinta-3">
                  {c.concessao} passa ao regime {c.proximo!.nome} em{" "}
                  {dia(c.proximo!.desde)}.
                </p>
              ))}
          </div>
        );
      })}
      <p className="text-[11px] text-tinta-3">
        O prazo aberto não muda quando o regime muda: ele foi calculado pelo
        regime que valia quando começou a contar.
      </p>
    </section>
  );
}
